import { Button, Card, Col, Row, Typography, Tag, Divider } from "antd";
import React from "react";
import AvailabilityIcon from "assets/availability.svg";
import { ArrowLeftOutlined, EditFilled } from "@ant-design/icons";
import { usePostJobs } from "api/jobs/usePostJob";
import { PartialJobInterface } from "api/jobs/dto";
import { useNavigate } from "react-router-dom";
import { useUpdateJob } from "api/jobs/useUpdateJob";
import { storage } from "utils/storage";
import { formatDate } from "utils/date";
import { JOB_STATUS } from "api/jobs/dto.ts";

const { Title, Text } = Typography;

type Props = {
  prev: () => void;
  next: () => void;
  formData: PartialJobInterface;
  setCurrent: React.Dispatch<React.SetStateAction<number>>;
  isEditMode: boolean;
  id: string | undefined;
};

const ReviewPage: React.FC<Props> = ({
  prev,
  formData,
  setCurrent,
  isEditMode,
  id,
}) => {
  const user = storage.getUserFromStorage();
  const postJobMutation = usePostJobs();
  const updateJobMutation = useUpdateJob();
  const navigate = useNavigate();

  const getStatusColor = (status: JOB_STATUS | undefined): string => {
    switch (status) {
      case JOB_STATUS.DRAFT:
        return "cyan";
      case JOB_STATUS.PUBLISHED:
        return "green";
      case JOB_STATUS.FILLED:
        return "geekblue";
      case JOB_STATUS.EXPIRED:
        return "red";
      default:
        return "default";
    }
  };

  const handlePostJob = () => {
    const payload = {
      ...formData,
      salary: {
        amount: formData.salary?.amount ?? 0,
        period: formData.salary?.period ?? "",
        isPubliclyAvailable: formData.salary?.isPubliclyAvailable ?? true,
        isNegotiated: formData.salary?.isNegotiated ?? false,
        currency: formData.salary?.currency ?? "NPR",
      },
      recruiter: user.uid,
      status: JOB_STATUS.PUBLISHED,
    };

    if (isEditMode && id) {
      updateJobMutation.mutate(
        { id: id, job: payload },
        {
          onSuccess: () => {
            navigate("/recruiter/jobs");
          },
        }
      );
    } else {
      postJobMutation.mutate(payload, {
        onSuccess: () => {
          navigate("/recruiter/jobs");
        },
      });
    }
  };

  return (
    <>
      <Card className="px-8 py-2 mt-12">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center">
          <Title level={2} style={{ fontWeight: 700 }}>
            Review the Job Post
          </Title>
          <img alt="#" src={AvailabilityIcon} />
        </div>
      </Card>
      <Card className="mt-6">
        <Title level={4} style={{ fontWeight: 700, marginBottom: "24px" }}>
          Job Details
          <Tag
            className="ms-3 align-middle italic text-base font-normal"
            color={getStatusColor(formData?.status)}
          >
            {" "}
            {formData?.status}
          </Tag>
        </Title>
        <Row gutter={[8, 28]}>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Job Title
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.jobTitle}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(0)}
            />
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Company Name
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.companyName}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(0)}
            />
          </Col>

          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Job Type
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.jobType}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(0)}
            />
          </Col>

          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Experience
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.experience}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(0)}
            />
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Job description
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.jobDescription}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(0)}
            />
          </Col>

          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Salary
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.salary?.amount}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(1)}
            />
          </Col>

          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Salary Period
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.salary?.period}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(1)}
            />
          </Col>

          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Valid Until
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formatDate(formData.deadlineAt)}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(1)}
            />
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Vacancy Number
            </Text>
          </Col>

          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.numberOfVacancy}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(1)}
            />
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Vacancy Locations
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.joblocation?.map(loc => {
                return loc.languages && loc.languages.length > 0
                  ? `${loc.district} (${loc.languages.join(", ")})`
                  : loc.district;
              }).join(", ")}


            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(1)}
            />
          </Col>

          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Skills
            </Text>
          </Col>

          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {formData?.skills?.join(", ")}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(1)}
            />
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text
              type="secondary"
              style={{ fontSize: "16px", fontWeight: 600 }}
            >
              Questions
            </Text>
          </Col>
          <Col xs={10} sm={10} md={8} lg={8} xl={8}>
            <Text type="secondary" style={{ fontSize: "16px" }}>
              {/* {formData?.questions?.length} Questions, Total{" "} */}
              {formData?.questions?.map((item, index) => (
                <div key={index}>
                  <Text type="secondary" style={{ fontSize: "16px" }}>
                    {item.question}
                  </Text>
                  <Divider />
                </div>
              ))}
            </Text>
          </Col>
          <Col
            xs={4}
            sm={4}
            md={8}
            lg={8}
            xl={8}
            style={{ textAlign: "center" }}
          >
            <EditFilled
              style={{ fontSize: "18px" }}
              onClick={() => setCurrent(0)}
            />
          </Col>
        </Row>

        <div className="flex justify-between pt-12">
          <Button
            className="h-12 px-8 border-2 border-border text-base flex items-center gap-2"
            onClick={prev}
          >
            <ArrowLeftOutlined />
            Back
          </Button>
          <Button
            className="h-12 px-8 border-2 border-border text-base flex items-center gap-2 bg-primary text-white border-none"
            htmlType="submit"
            type="primary"
            loading={
              isEditMode
                ? updateJobMutation.isPending
                : postJobMutation.isPending
            }
            onClick={handlePostJob}
          >
            {isEditMode ? "Publish Job" : "    Post Job"}
          </Button>
        </div>
      </Card>
    </>
  );
};

export default ReviewPage;
